import { MessageTypes } from "@tmi/types/enums/message-types";
import { createDefaultLogger, LogLevels } from "@/types/console-logger";
import {
    CACHE_SCHEMA_VERSION, CONTENT_COOLDOWN_KEYS, CONTENT_STORES, DATABASE_NAME, STATE_STORE, STORE_NAMES, type StoreName
} from "./cache-schema";

const logger = createDefaultLogger( LogLevels.DEBUG );

// Literal names, not `new.target.name`: the production build minifies class names, and these names are what
// logs and incident reports carry (F-M9).
export class CacheError extends Error {
    override name: string = 'CacheError';
    constructor( message: string, options?: { cause?: unknown } ) {
        super( message, options );
    }
}
export class CacheOpenError extends CacheError {
    override name: string = 'CacheOpenError';
}
export class CacheClosedError extends CacheError {
    override name: string = 'CacheClosedError';
}

/** How long an open or delete may sit in `blocked` (another context has not closed) before we give up and surface it. */
export const BLOCKED_TIMEOUT_MS: number = 10_000;
/** sessionStorage key recording the last VersionError recovery in THIS tab, so two live builds cannot ping-pong. */
export const RECOVERY_BREAKER_KEY = 'tmi-cache:recovered-at' as const;
export const RECOVERY_BREAKER_WINDOW_MS: number = 60_000;

export function requestToPromise< T >( request: IDBRequest< T > ): Promise< T > {
    return new Promise( ( resolve, reject ) => {
        request.onsuccess = () => resolve( request.result );
        request.onerror = () => reject( request.error ?? new DOMException( 'IndexedDB request failed', 'UnknownError' ) );
    } );
}

export function transactionDone( tx: IDBTransaction ): Promise< void > {
    return new Promise( ( resolve, reject ) => {
        tx.oncomplete = () => resolve();
        // A failed request's `error` event bubbles here BEFORE the transaction aborts and sets `tx.error`, so the
        // real cause (e.g. ConstraintError) lives on the event's target request (F-M2).
        tx.onerror = ( event: Event ) => reject(
            ( event?.target as IDBRequest | null )?.error ?? tx.error ?? new DOMException( 'IndexedDB transaction failed', 'UnknownError' )
        );
        tx.onabort = () => reject( tx.error ?? new DOMException( 'IndexedDB transaction aborted', 'AbortError' ) );
    } );
}

/**
 * Runs inside the versionchange transaction. Creates missing stores; when upgrading an existing database,
 * empties every content store and drops the cooldown keys in the same atomic unit. `cache-owner` is kept
 * on purpose: the same user's re-sync must not look like a change of hands.
 */
export function applyUpgrade( db: IDBDatabase, tx: IDBTransaction, oldVersion: number ): void {
    for ( const name of STORE_NAMES ) {
        if ( !db.objectStoreNames.contains( name ) ) {
            db.createObjectStore( name );
        }
    }
    if ( oldVersion > 0 ) {
        for ( const name of CONTENT_STORES ) {
            tx.objectStore( name ).clear();
        }
        const state = tx.objectStore( STATE_STORE );
        for ( const key of CONTENT_COOLDOWN_KEYS ) {
            state.delete( key );
        }
    }
}

// ---------------------------------------------------------------------------------------------------
// Eviction of THIS context when another context upgrades the database (F1).
// ---------------------------------------------------------------------------------------------------

export interface StaleContext {
    isWindow: boolean;
    reload(): void;
    postMessage( message: unknown ): void;
    closeWorker(): void;
}

function defaultStaleContext(): StaleContext {
    const isWindow = typeof window !== 'undefined';
    const worker = self as unknown as DedicatedWorkerGlobalScope;
    return {
        isWindow,
        reload: () => window.location.reload(),
        postMessage: message => worker.postMessage( message ),
        closeWorker: () => worker.close()
    };
}

let reloadTriggered = false;

/** Window: reload. Worker: tell the window and stop. Idempotent within one context lifetime (R49). */
export function handleStaleConnection( context: StaleContext = defaultStaleContext() ): void {
    if ( reloadTriggered ) {
        return;
    }
    reloadTriggered = true;
    if ( context.isWindow ) {
        logger.warn( '[ cache ] a newer build upgraded the database — reloading this window' );
        context.reload();
    } else {
        logger.warn( '[ cache ] a newer build upgraded the database — asking the window to reload' );
        context.postMessage( { eventType: MessageTypes.RELOAD_REQUIRED } );
        context.closeWorker();
    }
}

let staleHandler: () => void = () => handleStaleConnection();

/** Tests replace the eviction behaviour. */
export function onStaleConnection( handler: () => void ): void {
    staleHandler = handler;
}

// ---------------------------------------------------------------------------------------------------
// Incident reporting (F-I2). The logger is a no-op in production, so the three failure modes that change
// what a user sees are ALSO handed to an injected reporter. The main thread wires it to the app's
// client-error channel (plugins/CachePlugin.ts); this module never imports app code.
// ---------------------------------------------------------------------------------------------------

export type CacheIncident = {
    kind: 'version-error-recovery' | 'recovery-refused' | 'blocked-timeout';
    database: string;
    error?: unknown;
};

let incidentReporter: ( incident: CacheIncident ) => void = () => { /* default: the logger calls alone */ };

export function onCacheIncident( reporter: ( incident: CacheIncident ) => void ): void {
    incidentReporter = reporter;
}

/** Never throws: a broken reporter must not break the open/delete it is reporting on. */
function reportIncident( incident: CacheIncident ): void {
    try {
        incidentReporter( incident );
    } catch ( err: unknown ) {
        logger.warn( `[ cache ] the incident reporter threw while reporting "${ incident.kind }": ${ ( err as Error )?.message ?? err }` );
    }
}

// ---------------------------------------------------------------------------------------------------
// Connection state. `reloading` is set by versionchange and cleared ONLY by closeDatabase() (tests):
// once a newer build owns the database, nothing in this context may reopen it (R39).
// ---------------------------------------------------------------------------------------------------

let connection: IDBDatabase | null = null;
let opening: Promise< IDBDatabase > | null = null;
let reloading = false;

/** Only the window deletes-and-recreates on VersionError; the worker fails its pass and retries later (F36). */
let recoverFromVersionErrorHere: boolean = typeof window !== 'undefined';
export function setVersionErrorRecovery( enabled: boolean ): void {
    recoverFromVersionErrorHere = enabled;
}

// Both breaker helpers run inside `request.onerror`: a throw there would leave the open settled-but-unresolved
// forever. Storage that is unavailable (SecurityError, blocked site data) reads as "not recently recovered" and
// the write is skipped — recovery stays bounded by `reloading` and the per-context `opening` memo (F-M4).
function recoveredRecently(): boolean {
    try {
        if ( typeof sessionStorage === 'undefined' ) {
            return false;
        }
        const at = Number( sessionStorage.getItem( RECOVERY_BREAKER_KEY ) );
        return at > 0 && Date.now() - at < RECOVERY_BREAKER_WINDOW_MS;
    } catch {
        return false;
    }
}

function recordRecovery(): void {
    try {
        if ( typeof sessionStorage !== 'undefined' ) {
            sessionStorage.setItem( RECOVERY_BREAKER_KEY, String( Date.now() ) );
        }
    } catch {
        /* storage unavailable: skip the breaker write */
    }
}

export function deleteDatabase( name: string, blockedTimeoutMs: number = BLOCKED_TIMEOUT_MS ): Promise< void > {
    return new Promise( ( resolve, reject ) => {
        let settled = false;
        let timer: ReturnType< typeof setTimeout > | undefined;
        const request = indexedDB.deleteDatabase( name );
        request.onsuccess = () => { clearTimeout( timer ); if ( settled ) return; settled = true; resolve(); };
        request.onerror = () => { clearTimeout( timer ); if ( settled ) return; settled = true; reject( request.error ?? new DOMException( `deleteDatabase("${ name }") failed`, 'UnknownError' ) ); };
        request.onblocked = () => {
            logger.warn( `[ cache ] delete of "${ name }" is blocked until another context closes it` );
            timer ??= setTimeout( () => {
                settled = true;
                const error = new CacheOpenError( `[ cache ] delete of "${ name }" stayed blocked for ${ blockedTimeoutMs } ms` );
                reportIncident( { kind: 'blocked-timeout', database: name, error } );
                reject( error );
            }, blockedTimeoutMs );
        };
    } );
}

function openOnce( recoverFromVersionError: boolean ): Promise< IDBDatabase > {
    return new Promise( ( resolve, reject ) => {
        let settled = false;
        let blockedTimer: ReturnType< typeof setTimeout > | undefined;
        let request: IDBOpenDBRequest;
        try {
            request = indexedDB.open( DATABASE_NAME, CACHE_SCHEMA_VERSION );
        } catch ( err: unknown ) {
            // e.g. SecurityError where IndexedDB is disabled: keep the CacheError contract (F-M3).
            reject( new CacheOpenError( `[ cache ] could not open "${ DATABASE_NAME }" at v${ CACHE_SCHEMA_VERSION }: ${ ( err as { name?: string } | null )?.name ?? 'unknown error' }`, { cause: err } ) );
            return;
        }

        request.onupgradeneeded = ( event ) => {
            applyUpgrade( request.result, request.transaction!, event.oldVersion );
        };

        request.onblocked = () => {
            logger.warn( `[ cache ] upgrade to v${ CACHE_SCHEMA_VERSION } is waiting for another context to close its connection` );
            blockedTimer ??= setTimeout( () => {
                settled = true;
                const error = new CacheOpenError( `[ cache ] open of "${ DATABASE_NAME }" v${ CACHE_SCHEMA_VERSION } stayed blocked for ${ BLOCKED_TIMEOUT_MS } ms — another tab is holding the old version` );
                reportIncident( { kind: 'blocked-timeout', database: DATABASE_NAME, error } );
                reject( error );
            }, BLOCKED_TIMEOUT_MS );
        };

        request.onerror = () => {
            clearTimeout( blockedTimer );
            if ( settled ) return;
            settled = true;
            const error = request.error;
            if ( 'VersionError' === error?.name ) {
                if ( !recoverFromVersionError ) {
                    reject( new CacheOpenError( `[ cache ] "${ DATABASE_NAME }" is newer than this build (v${ CACHE_SCHEMA_VERSION }); the window recreates it — this pass is skipped`, { cause: error } ) );
                    return;
                }
                if ( reloading || recoveredRecently() ) {
                    reportIncident( { kind: 'recovery-refused', database: DATABASE_NAME, error } );
                    reject( new CacheOpenError( `[ cache ] "${ DATABASE_NAME }" is newer than this build (v${ CACHE_SCHEMA_VERSION }) and this tab already recovered within ${ RECOVERY_BREAKER_WINDOW_MS } ms — refusing to delete it again; close other TMI tabs or update the app`, { cause: error } ) );
                    return;
                }
                // This build is OLDER than the database: a rollback, or a stale precache. One deliberate reset.
                logger.error( `[ cache ] this build expects schema v${ CACHE_SCHEMA_VERSION } but "${ DATABASE_NAME }" is newer — deleting and recreating it` );
                reportIncident( { kind: 'version-error-recovery', database: DATABASE_NAME, error } );
                recordRecovery();
                deleteDatabase( DATABASE_NAME ).then( () => openOnce( false ) ).then( resolve, reject );
                return;
            }
            reject( new CacheOpenError( `[ cache ] could not open "${ DATABASE_NAME }" at v${ CACHE_SCHEMA_VERSION }: ${ error?.name ?? 'unknown error' }`, { cause: error } ) );
        };

        request.onsuccess = () => {
            clearTimeout( blockedTimer );
            const db = request.result;
            if ( settled ) {
                db.close();   // the blocked timeout already rejected; the next openDatabase() call opens fresh
                return;
            }
            settled = true;
            db.onversionchange = () => {
                db.close();
                connection = null;
                opening = null;
                reloading = true;
                staleHandler();
            };
            db.onclose = () => {
                connection = null;   // the browser force-closed us (storage cleared, etc.); reopen lazily
                opening = null;
            };
            resolve( db );
        };
    } );
}

/** Memoized on success only: a failed open is retried by the next call. Refuses to reopen after a versionchange. */
export function openDatabase(): Promise< IDBDatabase > {
    if ( reloading ) {
        return Promise.reject( new CacheClosedError( '[ cache ] the connection was closed because a newer build upgraded the database; a reload is in flight' ) );
    }
    if ( connection ) {
        return Promise.resolve( connection );
    }
    if ( !opening ) {
        opening = openOnce( recoverFromVersionErrorHere ).then(
            db => { connection = db; return db; },
            err => { opening = null; throw err; }
        );
    }
    return opening;
}

/** Tests only. */
export function closeDatabase(): void {
    connection?.close();
    connection = null;
    opening = null;
    reloading = false;
    reloadTriggered = false;
}

/**
 * One transaction over `stores`; resolves with `work`'s result once the transaction has COMMITTED. `work`
 * may only await IndexedDB requests issued on `tx`. A throw inside `work` aborts the transaction (F34/R40).
 */
export async function runTransaction< R >(
    stores: readonly StoreName[],
    mode: IDBTransactionMode,
    work: ( tx: IDBTransaction ) => R | Promise< R >
): Promise< R > {
    const db = await openDatabase();            // rejects with CacheClosedError once `reloading` is set
    // A versionchange can land between that resolution and this call, leaving `db` closed; `db.transaction()`
    // then throws InvalidStateError synchronously. Surface it as CacheClosedError so the stores' quiet-stop
    // guard sees a legitimate reload rather than a raw DOMException (R3a). ONLY `reloading` means a reload is
    // coming: a browser force-close (`onclose`: storage cleared, disk error) also leaves `db !== connection`,
    // but nothing will reload the page, so that is a plain CacheError and the next call reopens (F-I1).
    let tx: IDBTransaction;
    try {
        tx = db.transaction( stores as string[], mode );
    } catch ( err: unknown ) {
        if ( reloading ) {
            throw new CacheClosedError( '[ cache ] the connection closed before the transaction could start; a reload is in flight', { cause: err } );
        }
        throw new CacheError( `[ cache ] could not start a ${ mode } transaction over [ ${ stores.join( ', ' ) } ]`, { cause: err } );
    }
    const done = transactionDone( tx );
    done.catch( () => { /* observed below; prevents an unhandled rejection when work() throws first */ } );
    let result: R;
    try {
        result = await work( tx );
    } catch ( err: unknown ) {
        try { tx.abort(); } catch { /* already finished or aborting */ }
        throw err;
    }
    await done;
    return result;
}
